import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { CompositeDataSetMergeResumeParsingRecipe } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { CompositeDataSetMergeResumeParsingRecipeResumeGradingConfig } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { CompositeDataSetMergeResumeParsingRecipeResumeGradingRubric } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { CompositeDataSetRecipe } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { z } from 'zod';
import { DataSetCompositeRecipeDataWizardTracker } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { DataSetCompositeRecipeDataWizardTrackerItem } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { useDataSetCompositeRecipeDataWizardTracker } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { DataSetCompositeCompleteIndicator } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeCompleteIndicator';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Checkbox } from 'apps/sigma2-ui/src/components/ui/Checkbox';
import { SimpleInput } from 'apps/sigma2-ui/src/components/ui/SimpleInput';
import { WorkflowDataSetSelector } from 'apps/sigma2-ui/src/components/workflows/WorkflowDataSetSelector';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { DataSets } from 'libs/end-to-end/src/types/databases/data-sets/data-sets';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { isMergeAtsIntegrationDataSet } from 'libs/end-to-end/src/is-merge-ats-integration-data-set';
import { isMergeAtsIntegrationOrCompositeMergeFilteringDataSet } from 'libs/end-to-end/src/is-merge-ats-integration-data-set';
import { AllowedOpenAiModels } from 'libs/end-to-end/src/types/databases/data-sets/data-set-resume-rubric';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { useCrudOperations } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/use-crud-operations';
import { UseCrudOperationsPayload } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/use-crud-operations';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { SelectiveFilterDateBasedOptionComponent } from 'apps/sigma2-ui/src/SelectiveFilteringOptionComponent';
import { SelectiveFilterStringBasedOptionComponent } from 'apps/sigma2-ui/src/SelectiveFilteringOptionComponent';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { Modal } from 'apps/sigma2-ui/src/components/ui/Modal';
import { useRubricConstructsData } from 'apps/sigma2-ui/src/data-wizard/components/use-rubric-constructs-data';
import { UseRubricConstructsDataPayload } from 'apps/sigma2-ui/src/data-wizard/components/use-rubric-constructs-data';
import { DatabaseRubricBankConstructsView } from 'apps/sigma2-ui/src/data-wizard/components/DatabaseRubricBankConstructsView';

const MergeResumeParserConfigStep = z.enum([
  'parsing-config',
  'grading-config',
  'job-title-filtering',
  'applied-at-filtering',
  'final-review',
  'complete',
]);

type MergeResumeParserConfigStep = z.infer<typeof MergeResumeParserConfigStep>;

export const DataSetCompositeRecipeMergeResumeGradingConfigComponent = ({
  config,
  setConfig,
  rubricConfigOps,
  useRubricConstructsDataPayload,
  setCurrentRubricConfigIndex,
  setShowConstructsModal,
}: {
  config: CompositeDataSetMergeResumeParsingRecipeResumeGradingConfig;
  setConfig: (
    config: CompositeDataSetMergeResumeParsingRecipeResumeGradingConfig,
  ) => void;
  rubricConfigOps: UseCrudOperationsPayload<CompositeDataSetMergeResumeParsingRecipeResumeGradingRubric>;
  useRubricConstructsDataPayload: UseRubricConstructsDataPayload;
  setCurrentRubricConfigIndex: (index: number | null) => void;
  setShowConstructsModal: (show: boolean) => void;
}) => {
  return (
    <Spacing size={'lg'} direction={'vertical'}>
      <SelectBoxWithSearch
        type={'single'}
        setSelected={(value) => {
          setConfig({
            ...config,
            openAiModel: value as AllowedOpenAiModels,
          });
        }}
        selected={config.openAiModel}
        list={AllowedOpenAiModels.options.map((model) => {
          return {
            optionLabel: model,
            optionValue: model,
          };
        })}
        label={'OpenAI Model'}
      />

      <Button
        behavior={{
          type: 'onClick',
          onClick: () => {
            rubricConfigOps.onAdd({
              filter: {
                type: 'equals',
                value: '',
              },
              rubricConstructId: '',
            });
          },
        }}
        size={'small'}
        variant={'primary'}
        text={'Create'}
      />

      <Table
        items={config.rubricsByJobTitle ?? []}
        columnDefinitions={[
          {
            id: 'filter',
            header: () => 'Filter',
            cell: ({ item, rowIndex }) => (
              <SelectiveFilterStringBasedOptionComponent
                option={item.filter}
                setOption={(option) => {
                  rubricConfigOps.onEdit(rowIndex, {
                    filter: option,
                  });
                }}
              />
            ),
          },
          {
            id: 'rubric_item',
            header: () => 'Rubric Item',
            cell: ({ item, rowIndex }) => {
              const foundItem = useRubricConstructsDataPayload.constructs.find(
                (i) => i.id === item.rubricConstructId,
              );

              let itemIdItem: ReactNode;

              if (item.rubricConstructId === '' || !foundItem) {
                itemIdItem = <div>{'N/A'}</div>;
              } else {
                itemIdItem = <div>{foundItem.name}</div>;
              }

              return (
                <Spacing size={'md'} direction={'vertical'}>
                  {itemIdItem}
                  <Button
                    behavior={{
                      type: 'onClick',
                      onClick: () => {
                        useRubricConstructsDataPayload.singleSelectConfig?.setCurrentConstructId(
                          item.rubricConstructId,
                        );
                        setCurrentRubricConfigIndex(rowIndex);
                        setShowConstructsModal(true);
                      },
                    }}
                    size={'small'}
                    variant={'outline'}
                    text={'Select Item'}
                  />
                </Spacing>
              );
            },
          },
          {
            id: 'delete',
            header: () => <></>,
            cell: ({ rowIndex }) => {
              return (
                <Button
                  behavior={{
                    type: 'onClick',
                    onClick: () => {
                      rubricConfigOps.onDelete(rowIndex);
                    },
                  }}
                  variant={'destructive'}
                  size={'small'}
                  text={'Delete'}
                />
              );
            },
          },
        ]}
      />
    </Spacing>
  );
};

export const useRubricConstructsModalConfigs = ({
  organizationId,
  databaseId,
  rubricsByJobTitle,
  setRubricsByJobTitle,
}: {
  organizationId: string;
  databaseId: string;
  rubricsByJobTitle: CompositeDataSetMergeResumeParsingRecipeResumeGradingRubric[];
  setRubricsByJobTitle: (
    rubrics: CompositeDataSetMergeResumeParsingRecipeResumeGradingRubric[],
  ) => void;
}): {
  showConstructsModal: boolean;
  setShowConstructsModal: (show: boolean) => void;
  currentRubricConfigIndex: number | null;
  setCurrentRubricConfigIndex: (index: number | null) => void;
  useRubricConstructsDataPayload: UseRubricConstructsDataPayload;
  rubricConfigOps: UseCrudOperationsPayload<CompositeDataSetMergeResumeParsingRecipeResumeGradingRubric>;
} => {
  const [showConstructsModal, setShowConstructsModal] = useState(false);
  const [currentRubricConfigIndex, setCurrentRubricConfigIndex] = useState<
    number | null
  >(null);

  const useRubricConstructsDataPayload = useRubricConstructsData({
    organizationId,
    databaseId,
    isSelectEnabled: true,
  });

  const rubricConfigOps = useCrudOperations({
    items: rubricsByJobTitle,
    setItems: (items) => {
      setRubricsByJobTitle(items);
    }, // This will be set in the component
    postProcessNewItems: (newItems) => newItems,
  });

  return {
    showConstructsModal,
    setShowConstructsModal,
    useRubricConstructsDataPayload,
    rubricConfigOps,
    currentRubricConfigIndex,
    setCurrentRubricConfigIndex,
  };
};

export const RubricConstructsModal = ({
  showConstructsModal,
  setShowConstructsModal,
  currentRubricConfigIndex,
  setCurrentRubricConfigIndex,
  useRubricConstructsDataPayload,
  rubricConfigOps,
}: {
  showConstructsModal: boolean;
  setShowConstructsModal: (show: boolean) => void;
  currentRubricConfigIndex: number | null;
  setCurrentRubricConfigIndex: (index: number | null) => void;
  useRubricConstructsDataPayload: UseRubricConstructsDataPayload;
  rubricConfigOps: UseCrudOperationsPayload<CompositeDataSetMergeResumeParsingRecipeResumeGradingRubric>;
}) => {
  return (
    <Modal
      show={showConstructsModal}
      onClose={() => {
        setShowConstructsModal(false);
        setCurrentRubricConfigIndex(null);
      }}
      title={'Choose Rubric Items'}
      maxWidthOverride="max-w-screen-md"
      scrollable={true}
    >
      <DatabaseRubricBankConstructsView
        payload={useRubricConstructsDataPayload}
        isEditable={false}
      />

      <Button
        behavior={{
          type: 'onClick',
          onClick: () => {
            if (
              currentRubricConfigIndex !== null &&
              useRubricConstructsDataPayload.singleSelectConfig
                ?.currentConstructId
            ) {
              rubricConfigOps.onEdit(currentRubricConfigIndex, {
                rubricConstructId:
                  useRubricConstructsDataPayload.singleSelectConfig
                    .currentConstructId,
              });
            }

            setShowConstructsModal(false);
            setCurrentRubricConfigIndex(null);
          },
        }}
        variant={'primary'}
        size={'small'}
        text={'Save'}
      />
    </Modal>
  );
};

export const DataSetCompositeRecipeMergeResumeParserConfigComponent = ({
  organizationId,
  databaseId,
  config,
  setConfig,
}: {
  organizationId: string;
  databaseId: string;
  config: CompositeDataSetMergeResumeParsingRecipe;
  setConfig: (config: CompositeDataSetRecipe) => void;
}) => {
  const patternListDataSetsGetUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/data-sets`;
  }, [organizationId, databaseId]);

  const listDataSetsPattern = useSimpleReadPattern({
    responseSchema: DataSets,
    canRead: true,
    getUrlFn: patternListDataSetsGetUrlFn,
  });

  const stepItems: DataSetCompositeRecipeDataWizardTrackerItem<MergeResumeParserConfigStep>[] =
    useMemo(() => {
      return [
        { value: 'parsing-config', display: 'Parsing Config' },
        { value: 'grading-config', display: 'Grading Config' },
        { value: 'job-title-filtering', display: 'Job Title Filtering' },
        { value: 'applied-at-filtering', display: 'Applied At Filtering' },
        { value: 'final-review', display: 'Final Review' },
        { value: 'complete', display: 'Complete' },
      ];
    }, []);

  const dataWizardPayload =
    useDataSetCompositeRecipeDataWizardTracker<MergeResumeParserConfigStep>({
      items: stepItems,
      onNext: () => true,
    });

  const setFilteringConfig = useCallback(
    (
      configs: CompositeDataSetMergeResumeParsingRecipe['jobTitleSelectiveFilteringConfigs'],
    ) => {
      setConfig({
        ...config,
        jobTitleSelectiveFilteringConfigs: configs,
      });
    },
    [config, setConfig],
  );

  const filteringConfigOps = useCrudOperations({
    items: config.jobTitleSelectiveFilteringConfigs ?? [],
    setItems: setFilteringConfig,
  });

  const setAppliedAtFilteringConfig = useCallback(
    (
      configs: CompositeDataSetMergeResumeParsingRecipe['applicationAppliedAtFilteringConfigs'],
    ) => {
      setConfig({
        ...config,
        applicationAppliedAtFilteringConfigs: configs,
      });
    },
    [config, setConfig],
  );

  const appliedAtConfigOps = useCrudOperations({
    items: config.applicationAppliedAtFilteringConfigs ?? [],
    setItems: setAppliedAtFilteringConfig,
  });

  const rubricConfigOps = useCrudOperations({
    items: config.gradingConfig?.rubricsByJobTitle ?? [],
    setItems: (configs) => {
      if (config.gradingConfig) {
        setConfig({
          ...config,
          gradingConfig: {
            ...config.gradingConfig,
            rubricsByJobTitle: configs,
          },
        });
      }
    },
  });

  const [showConstructsModal, setShowConstructsModal] = useState(false);
  const [currentRubricConfigIndex, setCurrentRubricConfigIndex] = useState<
    number | null
  >(null);

  const useRubricConstructsDataPayload = useRubricConstructsData({
    organizationId,
    databaseId,
    isSelectEnabled: true,
  });

  const currentUiToShow = useMemo(() => {
    if (dataWizardPayload.selectedItem === null) {
      return null;
    }

    switch (dataWizardPayload.selectedItem) {
      case 'parsing-config':
        return (
          <Spacing size={'md'} direction={'vertical'}>
            <WorkflowDataSetSelector
              title={'Merge Dataset'}
              dataSetId={config.mergeDataSetId}
              setDataSetId={(id: string) => {
                setConfig({
                  ...config,
                  mergeDataSetId: id ? id : '',
                });
              }}
              listDataSetsPattern={listDataSetsPattern}
              filterFn={isMergeAtsIntegrationOrCompositeMergeFilteringDataSet}
            />

            <WorkflowDataSetSelector
              title={'Employee Candidate Filter'}
              dataSetId={config.employeeCandidatesFilterDataSetId}
              setDataSetId={(id: string) => {
                setConfig({
                  ...config,
                  employeeCandidatesFilterDataSetId: id,
                });
              }}
              listDataSetsPattern={listDataSetsPattern}
            />

            <S2Checkbox
              checked={config.isResumeParsingFilteringDisabled}
              onChange={() => {
                setConfig({
                  ...config,
                  isResumeParsingFilteringDisabled:
                    !config.isResumeParsingFilteringDisabled,
                });
              }}
            >
              Disable Resume Parsing Filtering (All Resumes Will Be Parsed)
            </S2Checkbox>

            <S2Checkbox
              checked={config.useV2Grading}
              onChange={() => {
                setConfig({
                  ...config,
                  useV2Grading: !config.useV2Grading,
                });
              }}
            >
              Use Resume Parser V2
            </S2Checkbox>

            <S2Checkbox
              checked={config.triggeredByDagster}
              onChange={() => {
                setConfig({
                  ...config,
                  triggeredByDagster: !config.triggeredByDagster,
                });
              }}
            >
              Triggered by Dagster (Skip filtering, use pre-injected files)
            </S2Checkbox>

            <SimpleInput
              label={'Resume Parsing Cache Buster'}
              value={config.resumeParsingCacheBusterVersion}
              onChange={(value: string) => {
                if (value !== null) {
                  setConfig({
                    ...config,
                    resumeParsingCacheBusterVersion: value,
                  });
                }
              }}
            />
          </Spacing>
        );
      case 'grading-config':
        return (
          <Spacing size={'md'} direction={'vertical'}>
            <S2Checkbox
              checked={config.gradingConfig !== null}
              onChange={() => {
                if (config.gradingConfig) {
                  setConfig({
                    ...config,
                    gradingConfig: null,
                  });
                } else {
                  setConfig({
                    ...config,
                    gradingConfig: {
                      rubricsByJobTitle: [],
                      openAiModel: 'gpt-4o-mini',
                    },
                  });
                }
              }}
            >
              Enable Resume Grading
            </S2Checkbox>
            {config.gradingConfig && (
              <Spacing size={'lg'} direction={'vertical'}>
                <SelectBoxWithSearch
                  type={'single'}
                  setSelected={(value) => {
                    if (config.gradingConfig) {
                      setConfig({
                        ...config,
                        gradingConfig: {
                          ...config.gradingConfig,
                          openAiModel: value as AllowedOpenAiModels,
                        },
                      });
                    }
                  }}
                  selected={config.gradingConfig.openAiModel}
                  list={AllowedOpenAiModels.options.map((model) => {
                    return {
                      optionLabel: model,
                      optionValue: model,
                    };
                  })}
                  label={'OpenAI Model'}
                />

                <Button
                  behavior={{
                    type: 'onClick',
                    onClick: () => {
                      rubricConfigOps.onAdd({
                        filter: {
                          type: 'equals',
                          value: '',
                        },
                        rubricConstructId: '',
                      });
                    },
                  }}
                  size={'small'}
                  variant={'primary'}
                  text={'Create'}
                />

                <Table
                  items={config.gradingConfig.rubricsByJobTitle ?? []}
                  columnDefinitions={[
                    {
                      id: 'filter',
                      header: () => 'Filter',
                      cell: ({ item, rowIndex }) => (
                        <SelectiveFilterStringBasedOptionComponent
                          option={item.filter}
                          setOption={(option) => {
                            rubricConfigOps.onEdit(rowIndex, {
                              filter: option,
                            });
                          }}
                        />
                      ),
                    },
                    {
                      id: 'rubric_item',
                      header: () => 'Rubric Item',
                      cell: ({ item, rowIndex }) => {
                        const foundItem =
                          useRubricConstructsDataPayload.constructs.find(
                            (i) => i.id === item.rubricConstructId,
                          );

                        let itemIdItem: ReactNode;

                        if (item.rubricConstructId === '' || !foundItem) {
                          itemIdItem = <div>{'N/A'}</div>;
                        } else {
                          itemIdItem = <div>{foundItem.name}</div>;
                        }

                        return (
                          <Spacing size={'md'} direction={'vertical'}>
                            {itemIdItem}
                            <Button
                              behavior={{
                                type: 'onClick',
                                onClick: () => {
                                  useRubricConstructsDataPayload.singleSelectConfig?.setCurrentConstructId(
                                    item.rubricConstructId,
                                  );
                                  setCurrentRubricConfigIndex(rowIndex);
                                  setShowConstructsModal(true);
                                },
                              }}
                              size={'small'}
                              variant={'outline'}
                              text={'Select Item'}
                            />
                          </Spacing>
                        );
                      },
                    },
                    {
                      id: 'delete',
                      header: () => <></>,
                      cell: ({ rowIndex }) => {
                        return (
                          <Button
                            behavior={{
                              type: 'onClick',
                              onClick: () => {
                                rubricConfigOps.onDelete(rowIndex);
                              },
                            }}
                            variant={'destructive'}
                            size={'small'}
                            text={'Delete'}
                          />
                        );
                      },
                    },
                  ]}
                />
              </Spacing>
            )}
          </Spacing>
        );
      case 'job-title-filtering':
        return (
          <Spacing size={'sm'} direction={'vertical'}>
            <Button
              size={'small'}
              behavior={{
                type: 'onClick',
                onClick: () => {
                  filteringConfigOps.onAdd({
                    type: 'equals',
                    value: '',
                  });
                },
              }}
              variant={'primary'}
              text={'Add'}
            />

            <Table
              items={config.jobTitleSelectiveFilteringConfigs ?? []}
              columnDefinitions={[
                {
                  id: 'type',
                  header: () => <></>,
                  cell: ({ item, rowIndex }) => {
                    return (
                      <SelectiveFilterStringBasedOptionComponent
                        option={item}
                        setOption={(newValue) => {
                          filteringConfigOps.onEdit(rowIndex, newValue);
                        }}
                      />
                    );
                  },
                },
                {
                  id: 'delete',
                  header: () => <></>,
                  cell: ({ rowIndex }) => {
                    return (
                      <Box
                        as={'div'}
                        display={'flex'}
                        flexDirection={'col'}
                        alignItems={'center'}
                      >
                        <Button
                          behavior={{
                            type: 'onClick',
                            onClick: () => {
                              filteringConfigOps.onDelete(rowIndex);
                            },
                          }}
                          variant={'destructive'}
                          size={'small'}
                          text={'Delete'}
                        />
                      </Box>
                    );
                  },
                },
              ]}
            />
          </Spacing>
        );
      case 'applied-at-filtering':
        return (
          <Spacing size={'sm'} direction={'vertical'}>
            <Button
              size={'small'}
              behavior={{
                type: 'onClick',
                onClick: () => {
                  appliedAtConfigOps.onAdd({
                    type: 'date-is-before',
                    date: '',
                  });
                },
              }}
              variant={'primary'}
              text={'Add'}
            />

            <Table
              items={config.applicationAppliedAtFilteringConfigs ?? []}
              columnDefinitions={[
                {
                  id: 'type',
                  header: () => <></>,
                  cell: ({ item, rowIndex }) => {
                    return (
                      <SelectiveFilterDateBasedOptionComponent
                        option={item}
                        setOption={(newValue) => {
                          appliedAtConfigOps.onEdit(rowIndex, newValue);
                        }}
                      />
                    );
                  },
                },
                {
                  id: 'delete',
                  header: () => <></>,
                  cell: ({ rowIndex }) => {
                    return (
                      <Box
                        as={'div'}
                        display={'flex'}
                        flexDirection={'col'}
                        alignItems={'center'}
                      >
                        <Button
                          behavior={{
                            type: 'onClick',
                            onClick: () => {
                              appliedAtConfigOps.onDelete(rowIndex);
                            },
                          }}
                          variant={'destructive'}
                          size={'small'}
                          text={'Delete'}
                        />
                      </Box>
                    );
                  },
                },
              ]}
            />
          </Spacing>
        );
      case 'final-review':
        return (
          <Spacing size={'md'} direction={'vertical'}>
            <div></div>
          </Spacing>
        );
      case 'complete':
        return <DataSetCompositeCompleteIndicator />;
    }
  }, [
    config,
    dataWizardPayload.selectedItem,
    listDataSetsPattern,
    setConfig,
    useRubricConstructsDataPayload,
    appliedAtConfigOps,
    filteringConfigOps,
    rubricConfigOps,
    setCurrentRubricConfigIndex,
    setShowConstructsModal,
    showConstructsModal,
    currentRubricConfigIndex,
  ]);

  return (
    <Spacing size={'lg'} direction={'vertical'}>
      <Modal
        show={showConstructsModal}
        onClose={() => {
          setShowConstructsModal(false);
          setCurrentRubricConfigIndex(null);
        }}
        title={'Choose Rubric Items'}
        maxWidthOverride="max-w-screen-md"
        scrollable={true}
      >
        <DatabaseRubricBankConstructsView
          payload={useRubricConstructsDataPayload}
          isEditable={false}
        />

        <Button
          behavior={{
            type: 'onClick',
            onClick: () => {
              if (
                currentRubricConfigIndex !== null &&
                useRubricConstructsDataPayload.singleSelectConfig
                  ?.currentConstructId
              ) {
                rubricConfigOps.onEdit(currentRubricConfigIndex, {
                  rubricConstructId:
                    useRubricConstructsDataPayload.singleSelectConfig
                      .currentConstructId,
                });
              }

              setShowConstructsModal(false);
              setCurrentRubricConfigIndex(null);
            },
          }}
          variant={'primary'}
          size={'small'}
          text={'Save'}
        />
      </Modal>
      <DataSetCompositeRecipeDataWizardTracker payload={dataWizardPayload} />
      {currentUiToShow}
      {dataWizardPayload.paginationButtons}
    </Spacing>
  );
};
